// components/delete-space-confirm.js
// ═══════════════════════════════════════════════════════════════
// Shared confirm dialog for deleting / leaving a space or
// workspace. Shows an itemized count of what will be permanently
// removed before the user clicks the destructive button — so they
// know exactly what they're losing.
//
// Two modes (driven by `isCreator`):
//   true  → "Delete this space?" — irreversible, wipes everything
//   false → "Leave this space?"  — only removes this user
//
// Counts are fetched lazily from /api/user/space-stats once the
// dialog opens. While loading, shows a placeholder line. If the
// fetch fails, falls back to the previous generic copy.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

export default function DeleteSpaceConfirm({
  spaceId,
  spaceName,
  isCreator,
  supabase,
  onCancel,
  onConfirm,
}) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!spaceId || !supabase || !isCreator) {
      // Leave-only path doesn't need counts — only removes this user.
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `/api/user/space-stats?spaceId=${encodeURIComponent(spaceId)}`,
          { headers: { 'Authorization': `Bearer ${session?.access_token || ''}` } },
        );
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (e) {
        console.debug('[delete-confirm] stats fetch failed:', e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [spaceId, supabase, isCreator]);

  // Build the itemized list. Skip rows with count = 0 so the
  // dialog doesn't say "0 messages, 0 files" for empty spaces.
  const items = stats ? [
    { label: stats.messages === 1 ? 'message' : 'messages', count: stats.messages },
    { label: stats.artifacts === 1 ? 'artifact' : 'artifacts', count: stats.artifacts },
    { label: stats.files === 1 ? 'uploaded file' : 'uploaded files', count: stats.files },
    { label: stats.nodes === 1 ? 'canvas node' : 'canvas nodes', count: stats.nodes },
  ].filter(i => i.count > 0) : [];

  const hasOtherParticipants = stats && stats.participants > 1;

  return (
    <div
      className="shift-modal-overlay"
      style={{
        zIndex: 2000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div style={{
        background: '#07070a',
        border: '1px solid #272736',
        borderRadius: 12,
        width: 420,
        padding: '24px',
        fontFamily: 'Georgia, serif',
      }}>
        <div style={{ fontSize: 14, color: '#ddd', marginBottom: 8 }}>
          {isCreator ? 'Delete this space?' : 'Leave this space?'}
        </div>

        {spaceName && (
          <div style={{ fontSize: 12, color: '#888', marginBottom: 14, fontStyle: 'italic' }}>
            "{spaceName}"
          </div>
        )}

        {isCreator ? (
          <>
            <div style={{ fontSize: 12, color: '#888', lineHeight: 1.6, marginBottom: 10 }}>
              This will permanently delete:
            </div>
            <div style={{ marginBottom: 16, paddingLeft: 4 }}>
              {loading && (
                <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic' }}>
                  counting…
                </div>
              )}
              {!loading && items.length === 0 && (
                <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic' }}>
                  the space record (no content yet)
                </div>
              )}
              {!loading && items.length > 0 && items.map((it, i) => (
                <div key={i} style={{
                  fontSize: 12,
                  color: '#aaa',
                  lineHeight: 1.7,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                }}>
                  <span style={{ color: '#c46050', fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
                    {it.count}
                  </span>
                  <span>{it.label}</span>
                </div>
              ))}
            </div>
            {hasOtherParticipants && (
              <div style={{
                fontSize: 11,
                color: '#c46050',
                lineHeight: 1.5,
                marginBottom: 16,
                fontStyle: 'italic',
              }}>
                The other participant{stats.participants > 2 ? 's' : ''} will also lose access.
              </div>
            )}
            <div style={{
              fontSize: 11,
              color: '#666',
              lineHeight: 1.5,
              marginBottom: 20,
              fontStyle: 'italic',
            }}>
              This cannot be undone.
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#888', lineHeight: 1.6, marginBottom: 20, fontStyle: 'italic' }}>
            You'll lose access to this conversation. The other person can still see it.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: '1px solid #272736',
              borderRadius: 6,
              color: '#666',
              fontSize: 11,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: 'Georgia, serif',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: 'rgba(196,96,80,0.08)',
              border: '1px solid rgba(196,96,80,0.3)',
              borderRadius: 6,
              color: '#c46050',
              fontSize: 11,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: 'Georgia, serif',
            }}
          >
            {isCreator ? 'Delete forever' : 'Leave'}
          </button>
        </div>
      </div>
    </div>
  );
}
